import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import axios from 'axios';

import NativeTable from '../../components/NativeTable/NativeTable';
import Loader from '../../components/Loader';
import Widget from '../../components/Widget';

import { getWorkperiodsRequest, startWorkperiodRequest, endWorkperiodRequest } from '../../actions/sales';
import { withTranslation } from '../../i18n/LanguageContext';

class WorkperiodsList extends React.Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        workperiods: PropTypes.array,
        workperiodsLoading: PropTypes.bool,
        workperiodToggling: PropTypes.bool,
    };

    static defaultProps = {
        workperiods: [],
        workperiodsLoading: false,
        workperiodToggling: false,
    };

    isActivePeriod = (row) => {
        const endUtc = row.endUtc || row.EndUtc || row.end || row.End;
        if (!endUtc) return true;
        const s = String(endUtc).trim();
        return s === '' || s.startsWith('0001-01-01');
    };

    handleStartWorkperiod = () => {
        if (this.props.workperiodToggling) return;
        if (!window.confirm('Start a new work period now?')) return;
        this.props.dispatch(startWorkperiodRequest(() => {
            // Refresh the list so the newly-started period appears.
            const { startDate, endDate } = this.state;
            this.props.dispatch(getWorkperiodsRequest(startDate, endDate));
            this.refreshActiveWorkperiod();
        }));
    };

    handleEndWorkperiod = (periodId) => {
        if (this.props.workperiodToggling) return;
        if (!window.confirm('End the current work period and clock out all active users?')) return;
        this.props.dispatch(endWorkperiodRequest(periodId, () => {
            const { startDate, endDate } = this.state;
            this.props.dispatch(getWorkperiodsRequest(startDate, endDate));
            this.refreshActiveWorkperiod();
        }));
    };

    // Authoritative check: /api/admin/workperiod/current returns the
    // currently-open work period (if any). The list endpoint above may
    // return only closed periods within the date range, so we cannot rely
    // on it to detect an active period — e.g. when the page first loads
    // and there are no closed periods yet, the toggle would otherwise
    // never know to show "End Work Period".
    refreshActiveWorkperiod = async () => {
        try {
            const res = await axios.get('/api/admin/workperiod/current');
            const data = res && res.data;
            const wp = (data && (data.workPeriod || data.WorkPeriod)) || null;
            const id = wp ? (wp.PeriodId || wp.WorkperiodId || wp.WorkPeriodId || wp.periodId || wp.Id || wp.id) : 0;
            this.setState({ activePeriodIdLive: id || 0 });
        } catch (e) {
            this.setState({ activePeriodIdLive: 0 });
        }
    };

    getDefaultStartDate() {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    }

    getDefaultEndDate() {
        return new Date().toISOString().split('T')[0];
    }

    state = {
        startDate: this.getDefaultStartDate(),
        endDate: this.getDefaultEndDate(),
        searchTerm: '',
        // null = not yet fetched, 0 = no active period, >0 = active period id.
        // Set by refreshActiveWorkperiod().
        activePeriodIdLive: null,
    };

    componentDidMount() {
        const { startDate, endDate } = this.state;
        this.props.dispatch(getWorkperiodsRequest(startDate, endDate));
        this.refreshActiveWorkperiod();
    }

    handleStartDateChange = (e) => {
        const startDate = e.target.value;
        this.setState({ startDate }, () => {
            this.props.dispatch(getWorkperiodsRequest(startDate, this.state.endDate));
        });
    }

    handleEndDateChange = (e) => {
        const endDate = e.target.value;
        this.setState({ endDate }, () => {
            this.props.dispatch(getWorkperiodsRequest(this.state.startDate, endDate));
        });
    }

    formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') return '$0.00';
        const n = Number(value);
        if (Number.isNaN(n)) return '$0.00';
        return `$${n.toFixed(2)}`;
    }

    // List StartUtc is true UTC from the API (unlike detail Start/ClockIn which are merchant-local).
    parseUtc = (raw) => {
        if (!raw) return null;
        let str = String(raw).trim();
        // If no timezone indicator, append Z so JS parses as UTC
        if (!str.endsWith('Z') && !str.match(/[+-]\d{2}:\d{2}$/) && !str.match(/[+-]\d{4}$/)) {
            str += 'Z';
        }
        const d = new Date(str);
        return Number.isNaN(d.getTime()) ? null : d;
    }

    formatPeriod = (row) => {
        const raw = row.startUtc || row.StartUtc || row.startutc || row.label || row.Label;
        const date = this.parseUtc(raw);
        if (!date) return String(raw || '');
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const dayName = days[date.getDay()];
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = String(date.getFullYear()).slice(-2);
        return `${dayName}, ${month}/${day}/${year}`;
    }

    getNumber = (row, keys, fallback = 0) => {
        for (const key of keys) {
            const v = row[key];
            if (v !== null && v !== undefined && v !== '') {
                const n = Number(v);
                if (!Number.isNaN(n)) return n;
            }
        }
        return fallback;
    }

    formatAmountWithCount = (amount, count) => {
        const amountStr = this.formatCurrency(amount);
        const countNum = Number(count);
        if (Number.isNaN(countNum)) return amountStr;
        return `${amountStr} (${countNum})`;
    }

    handleSearchChange = (e) => {
        this.setState({ searchTerm: e.target.value });
    }

    matchesSearch = (row, term) => {
        if (!term || !term.trim()) return true;
        const t = term.trim().toLowerCase();

        // Period column (formatted day name + date, e.g. "Mon, 3/16/26")
        if (this.formatPeriod(row).toLowerCase().includes(t)) return true;

        const checkCurrency = (v) => v !== null && v !== undefined && this.formatCurrency(v).toLowerCase().includes(t);
        const checkNum    = (v) => v !== null && v !== undefined && String(v).toLowerCase().includes(t);

        if (checkNum(row.transactions ?? row.Transactions)) return true;
        if (checkNum(row.items ?? row.Items)) return true;
        if (checkCurrency(row.charges ?? row.Charges)) return true;
        if (checkCurrency(row.net ?? row.Net)) return true;
        if (checkCurrency(row.tipsCard ?? row.TipsCard)) return true;
        const cardFees = row.card || row.Card || {};
        if (checkCurrency(cardFees.fee ?? cardFees.Fee)) return true;
        if (checkCurrency(row.tipOuts ?? row.TipOuts)) return true;
        if (checkCurrency(row.refunds ?? row.Refunds)) return true;
        if (checkCurrency(row.netSalesMerch ?? row.NetSalesMerch)) return true;
        if (checkCurrency(row.merchRefunds ?? row.MerchRefunds)) return true;
        if (checkCurrency(row.tax ?? row.Tax)) return true;
        if (checkCurrency(row.gratuityCard ?? row.GratuityCard)) return true;

        const card = row.card || row.Card || {};
        if (this.formatAmountWithCount(card.amount ?? card.Amount, card.count ?? card.Count).toLowerCase().includes(t)) return true;

        const cash = row.cash || row.Cash || {};
        if (this.formatAmountWithCount(cash.amount ?? cash.Amount, cash.count ?? cash.Count).toLowerCase().includes(t)) return true;

        return false;
    }

    hasSales = (row) => {
        const charges = row.charges ?? row.Charges ?? 0;
        const transactions = row.transactions ?? row.Transactions ?? 0;
        const net = row.net ?? row.Net ?? 0;
        return Number(charges) > 0 || Number(transactions) > 0 || Number(net) > 0;
    }

    // Fully settled tips/gratuity → mute row text. $0 tips+grat is settled
    // (nothing to pay). Any owed (incl. pending_paid_to_manager) stays white.
    isTipsFullySettled = (row) => {
        if (!row) return false;
        if (row.tipsFullySettled !== undefined || row.TipsFullySettled !== undefined) {
            const flag = row.tipsFullySettled ?? row.TipsFullySettled;
            return flag === true || flag === 1 || flag === 'true' || flag === 'True';
        }
        const tips = Number(row.tipsCard ?? row.TipsCard ?? 0) || 0;
        const grat = Number(row.gratuityCard ?? row.GratuityCard ?? 0) || 0;
        const owed = Number(row.tipsOwedAmount ?? row.TipsOwedAmount ?? NaN);
        if (tips + grat <= 0.01) return true;
        return Number.isFinite(owed) && owed <= 0.01;
    }

    isInDateRange = (row, startDate, endDate) => {
        const raw = row.startUtc || row.StartUtc || row.startutc || row.label || row.Label;
        if (!raw) return true;
        const rowDate = this.parseUtc(raw);
        if (!rowDate) return true;
        const rowDateStr = `${rowDate.getFullYear()}-${String(rowDate.getMonth() + 1).padStart(2, '0')}-${String(rowDate.getDate()).padStart(2, '0')}`;
        if (startDate && rowDateStr < startDate) return false;
        if (endDate && rowDateStr > endDate) return false;
        return true;
    }

    render() {
        const { workperiods, workperiodsLoading } = this.props;
        const { startDate, endDate, searchTerm } = this.state;
        const filteredWorkperiods = workperiods
            .filter(this.hasSales)
            .filter(row => this.isInDateRange(row, startDate, endDate))
            .filter(row => this.matchesSearch(row, searchTerm));

        const activeFromList = workperiods.find(this.isActivePeriod) || null;
        // Source of truth is the dedicated /api/admin/workperiod/current
        // endpoint. Only fall back to scanning the list when that call
        // hasn't completed yet (activePeriodIdLive === null). Once it
        // returns 0 we trust it — otherwise the list could still report
        // a stale "active" row right after ending a period.
        let activePeriodId;
        if (this.state.activePeriodIdLive === null) {
            activePeriodId = activeFromList
                ? (activeFromList.periodId ?? activeFromList.PeriodId ?? activeFromList.id ?? activeFromList.Id)
                : null;
        } else {
            activePeriodId = this.state.activePeriodIdLive || null;
        }
        const hasActive = !!activePeriodId;

        return (
            <div className="page-wrapper">
                <div className="page-content">
                    {workperiodsLoading ? <Loader size={80} /> : null}

                    <div className="tab_header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                        <h1 style={{ margin: 0 }}>
                            <span className="d-block">Work Periods</span>
                        </h1>
                        {hasActive ? (
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => this.handleEndWorkperiod(activePeriodId)}
                                disabled={this.props.workperiodToggling}
                                style={{
                                    background: '#c0392b',
                                    color: '#fff',
                                    fontWeight: 700,
                                    opacity: this.props.workperiodToggling ? 0.7 : 1,
                                    cursor: this.props.workperiodToggling ? 'not-allowed' : 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <i className="zmdi zmdi-stop" />
                                {this.props.workperiodToggling ? this.props.t('workperiods.ending') : this.props.t('workperiods.end')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={this.handleStartWorkperiod}
                                disabled={this.props.workperiodToggling}
                                style={{
                                    background: '#7e57c2',
                                    color: '#fff',
                                    fontWeight: 700,
                                    opacity: this.props.workperiodToggling ? 0.7 : 1,
                                    cursor: this.props.workperiodToggling ? 'not-allowed' : 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <i className="zmdi zmdi-play-circle-outline" />
                                {this.props.workperiodToggling ? this.props.t('workperiods.starting') : this.props.t('workperiods.start')}
                            </button>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', opacity: 0.7 }}>{this.props.t('workperiods.startDate')}</label>
                            <input
                                type="date"
                                className="form-control"
                                value={startDate}
                                onChange={this.handleStartDateChange}
                                style={{ minWidth: '160px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', opacity: 0.7 }}>{this.props.t('workperiods.endDate')}</label>
                            <input
                                type="date"
                                className="form-control"
                                value={endDate}
                                onChange={this.handleEndDateChange}
                                style={{ minWidth: '160px' }}
                            />
                        </div>
                        <div style={{ flex: '1', minWidth: '180px' }}>
                            <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '4px', opacity: 0.7 }}>{this.props.t('workperiods.search')}</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={this.props.t('workperiods.searchPlaceholder')}
                                value={searchTerm}
                                onChange={this.handleSearchChange}
                            />
                        </div>
                    </div>

                    <style>{`
                        @keyframes wpl-pulse {
                            0%   { transform: scale(1);   opacity: 1; }
                            50%  { transform: scale(1.6); opacity: 0.4; }
                            100% { transform: scale(1);   opacity: 1; }
                        }
                        .wpl-dot {
                            display: inline-block;
                            width: 8px;
                            height: 8px;
                            border-radius: 50%;
                            background: #2ecc71;
                            animation: wpl-pulse 1.4s ease-in-out infinite;
                            flex-shrink: 0;
                        }
                        .wpl-tips-settled,
                        .wpl-tips-settled td,
                        .wpl-tips-settled td strong {
                            color: #a4a0a0 !important;
                        }
                        .wpl-tips-settled a {
                            color: #7e57c2 !important;
                        }
                    `}</style>
                    <Widget>
                        <div className="order_list">
                            <NativeTable
                                data={filteredWorkperiods}
                                getRowClassName={(row) => (this.isTipsFullySettled(row) ? 'wpl-tips-settled' : '')}
                                getRowStyle={(row) => (this.isTipsFullySettled(row) ? { color: '#a4a0a0' } : undefined)}
                                columns={[
                                    {
                                        key: 'label',
                                        header: this.props.t('workperiods.period'),
                                        sticky: true,
                                        render: (cell, row) => {
                                            const id = row.periodId ?? row.PeriodId ?? row.id ?? row.Id;
                                            const active = this.isActivePeriod(row);
                                            return (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                    {active && <span className="wpl-dot" title={this.props.t('workperiods.active')} />}
                                                    <Link
                                                        to={`/admin/sales/${id}`}
                                                        style={{ color: '#7e57c2', fontWeight: 700, textDecoration: 'none' }}
                                                    >
                                                        {this.formatPeriod(row)}
                                                    </Link>
                                                </span>
                                            );
                                        }
                                    },
                                    {
                                        key: 'transactions',
                                        header: this.props.t('workperiods.trans'),
                                        render: (cell, row) => row.transactions ?? row.Transactions ?? 0
                                    },
                                    {
                                        key: 'charges',
                                        header: this.props.t('workperiods.charges'),
                                        render: (cell, row) => this.formatCurrency(row.charges ?? row.Charges)
                                    },
                                    {
                                        key: 'net',
                                        header: this.props.t('workperiods.net'),
                                        render: (cell, row) => (
                                            <strong>{this.formatCurrency(row.net ?? row.Net)}</strong>
                                        )
                                    },
                                    {
                                        key: 'items',
                                        header: this.props.t('workperiods.items'),
                                        render: (cell, row) => row.items ?? row.Items ?? 0
                                    },
                                    {
                                        key: 'card',
                                        header: this.props.t('workperiods.card'),
                                        render: (cell, row) => {
                                            const c = row.card || row.Card || {};
                                            // Count only — amount equals CARD NET (card-like merch).
                                            return this.formatAmountWithCount(
                                                c.amount ?? c.Amount,
                                                c.count ?? c.Count
                                            );
                                        }
                                    },
                                    {
                                        key: 'cash',
                                        header: this.props.t('workperiods.cash'),
                                        render: (cell, row) => {
                                            const c = row.cash || row.Cash || {};
                                            return this.formatAmountWithCount(
                                                c.amount ?? c.Amount,
                                                c.count ?? c.Count
                                            );
                                        }
                                    },
                                    {
                                        key: 'tipsCard',
                                        header: this.props.t('workperiods.tipsCard'),
                                        render: (cell, row) => this.formatCurrency(row.tipsCard ?? row.TipsCard)
                                    },
                                    {
                                        key: 'tipOuts',
                                        header: this.props.t('workperiods.tipOuts'),
                                        render: (cell, row) => this.formatCurrency(row.tipOuts ?? row.TipOuts)
                                    },
                                    {
                                        key: 'refunds',
                                        header: this.props.t('workperiods.ref'),
                                        render: (cell, row) => this.formatCurrency(row.refunds ?? row.Refunds)
                                    },
                                    {
                                        key: 'netSalesMerch',
                                        header: this.props.t('workperiods.netSalesMerch'),
                                        render: (cell, row) => {
                                            const merchRefunds = Number(row.merchRefunds ?? row.MerchRefunds ?? 0);
                                            const net = row.netSalesMerch ?? row.NetSalesMerch;
                                            if (!(merchRefunds > 0) && (net === undefined || net === null)) {
                                                return '—';
                                            }
                                            const estimated = row.merchRefundEstimated ?? row.MerchRefundEstimated;
                                            return (
                                                <span title={estimated ? this.props.t('report.refundEstimateFootnote') : undefined}>
                                                    {this.formatCurrency(net)}
                                                    {estimated ? ' *' : ''}
                                                </span>
                                            );
                                        }
                                    },
                                    {
                                        key: 'tax',
                                        header: this.props.t('workperiods.tax'),
                                        render: (cell, row) => this.formatCurrency(row.tax ?? row.Tax)
                                    },
                                    {
                                        key: 'gratuityCard',
                                        header: this.props.t('workperiods.grat'),
                                        render: (cell, row) => this.formatCurrency(row.gratuityCard ?? row.GratuityCard)
                                    }
                                ]}
                            />
                        </div>
                    </Widget>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        workperiods: state.sales?.workperiods || [],
        workperiodsLoading: state.sales?.workperiodsLoading || false,
        workperiodToggling: state.sales?.workperiodToggling || false,
    };
}

export default withRouter(connect(mapStateToProps)(withTranslation(WorkperiodsList)));
